import { useUpdateLessonTag } from "hooks/useUpdateLessonTag";
import { LessonFlat } from "models/LessonFlat";
import { Box, CircularProgress, IconButton, Stack, styled, Typography, useTheme } from "@mui/material";
import MUIDataTable, { MUIDataTableColumnDef } from "mui-datatables";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import TagSelect from "components/TagSelect/TagSelect";
import { useTranslation } from "react-i18next";
import { LessonTagType } from "api/models/request/IUpdateLessonTagRequest";
import { LessonContextMenu } from "components/LessonContextMenu";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDeleteManyLessons } from "hooks/useDeleteManyLessons";
import { ConfirmationDialog } from "components/ConfirmationDialog";
import { createPrefixedPropsFilteredComponent } from "helpers/styledHelpers";
import { MakeSortableHead } from "helpers/sortTableHeaderHelper";

interface LessonsDataTableProps {
    setParentLessonsSelected: Dispatch<SetStateAction<number[]>>;
    lessons: LessonFlat[];
    fetchingLessons: boolean;
    title: string;
}

const StyledMUIDataTable = styled(MUIDataTable)`
  & .tss-1cdcmys-MUIDataTable-responsiveBase {
    overflow: visible !important;
  }
  & .MuiTableCell-head span button {
    font-size: 16px !important;
  }
`;

const StyledLessonName = createPrefixedPropsFilteredComponent<{$textAlign: string }>(Typography)(({ $textAlign }) => ({
    textAlign: $textAlign
}));
  
const StyledTableHeader = styled('th')({
textAlign: 'left'
});

const EditStyledTableHeader = styled('th')({
textAlign: 'center'
});

const StyledActionsContainer = styled(Box)({
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center'
});

export const LessonsDataTable = (props: LessonsDataTableProps ) => {
    const [lessons, setLessons] = useState<LessonFlat[]>([]);
    const [lessonsSelected, setLessonsSelected] = useState<number[]>([]);
    const [setSelectedRows, setSetSelectedRows] = useState<(rows: number[]) => void>(() => () => {});
    const [lessonTagsA, setLessonTagsA] = useState<string[]>([]);
    const [lessonTagsB, setLessonTagsB] = useState<string[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMessage, setDialogMessage] = useState("");
    const [confirmAction, setConfirmAction] = useState<() => void>(() => {});
    const { t } = useTranslation();
    const { updateLessonTag, updatingLessonTag} = useUpdateLessonTag();
    const { deleteManyLessons, deletingManyLessons } = useDeleteManyLessons();
    const { title, fetchingLessons, setParentLessonsSelected } = props;
    const theme = useTheme();

    useEffect(() => {
        const tagsA = new Set<string>();
        const tagsB = new Set<string>();
        lessons.forEach(l => {
            tagsA.add(l.tagA);
            tagsB.add(l.tagB);
        });
        setLessonTagsA(Array.from(tagsA));
        setLessonTagsB(Array.from(tagsB));
    }, [lessons]);

    useEffect(() => {
        setLessons(props.lessons);
    }, [props.lessons]);
    
    const handleDeleteSingleLesson = async (lessonId: string) => {
        setLessons(prevLessons => prevLessons.filter(lesson => lesson.id !== lessonId));
        setSelectedRows([]);
    };
    
    const lessonColumns = [
        { name: "id", label: "Id", options: { filter: false, sort: false, display: 'excluded' } },
        {
            name: "name",
            label: t('trainingsManagement.nameColumn'),
            options: {
                filter: true,
                sort: true,
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => {                    
                    return (
                        <StyledLessonName $textAlign={'left'}>
                            {value}
                        </StyledLessonName>
                    );
                },
                customHeadRender: ( columnMeta, _handleToggleColumn, sortOrder) => {
                         return (
                             <StyledTableHeader key={columnMeta.index} onClick={() => _handleToggleColumn(columnMeta.index)}>
                               <MakeSortableHead columnMeta={columnMeta} sortOrder={sortOrder}>
                                 <Typography paddingLeft={theme.spacing(1)} textAlign={'left'} fontWeight={'500'}>{columnMeta.label}</Typography>
                               </MakeSortableHead>
                             </StyledTableHeader>
                         );
                },
            }
        },
        {
            name: "tagA", 
            label: t('trainingsManagement.tagAColumn'),
            options: {
                filter: true,
                sort: true,
                filterOptions: {
                    names: [t('trainingsManagement.tagAemptyFilter'), ...lessonTagsA],
                    logic(tagA: any, filterVal: any) {
                        const isTagBEmpty = tagA === '' || tagA === undefined || tagA === null;
                        const filterIsEmpty = filterVal.indexOf(t('trainingsManagement.tagAemptyFilter')) >= 0;
                        const filterByValue = filterVal.indexOf(tagA) >= 0;
                
                        if (filterIsEmpty && isTagBEmpty) {
                            return false;
                        } else if (filterByValue) {
                            return false;
                        }
                        return true;
                    },
                },
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
                    return (
                        <TagSelect
                        options={lessonTagsA}
                        selectedOption={value}
                        onChange={(value: string) => handleUpdateLessonTag(tableMeta.rowData[0], value, LessonTagType.A)}
                        noOptionsText={t('lessonsTable.tagsSelectNoRowsText')}/>
                    )
                }                
            }
        },
        {
            name: "tagB",
            label: t('trainingsManagement.tagBColumn'),
            options: {
                filter: true,
                sort: true,
                filterOptions: {
                    names: [t('trainingsManagement.tagBemptyFilter'), ...lessonTagsB],
                    logic(tagB: any, filterVal: any) {
                        const isTagBEmpty = tagB === '' || tagB === undefined || tagB === null;
                        const filterIsEmpty = filterVal.indexOf(t('trainingsManagement.tagBemptyFilter')) >= 0;
                        const filterByValue = filterVal.indexOf(tagB) >= 0;
                
                        if (filterIsEmpty && isTagBEmpty) {
                            return false;
                        } else if (filterByValue) {
                            return false;
                        }
                        return true;
                    },
                },
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
                        return (
                            <TagSelect
                            options={lessonTagsB}
                            selectedOption={value}
                            onChange={(value: string) => handleUpdateLessonTag(tableMeta.rowData[0], value, LessonTagType.B)}
                            noOptionsText={t('lessonsTable.tagsSelectNoRowsText')}/>
                        )
                }
            }
        },
        {   name: 'actions',
            label: t('trainingsManagement.actions'),
            options: {
                filter: false,
                sort: false,
                display: 'true',
                responsive: 'standard',
                customHeadRender: ({ index, ...columnMeta }: any) => {
                    return (
                    <EditStyledTableHeader key={index}>
                        <EditIcon />
                    </EditStyledTableHeader>
                    );
                },
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
                    return (
                        <StyledActionsContainer>
                            <LessonContextMenu 
                                lessonId={tableMeta.rowData[0]} 
                                lessonName={tableMeta.rowData[1]} 
                                onLessonDelete={handleDeleteSingleLesson} 
                            />
                        </StyledActionsContainer>
                    )}
                } }
    ] as MUIDataTableColumnDef[];

    const handleUpdateLessonTag = async (lessonId: string, tag: string, tagType: LessonTagType) => {
        const resp = await updateLessonTag({ lessonId, newTag: tag, tagType });
        if (resp?.success) {
          setLessons((prevLessons) => {
            return prevLessons.map((lesson) => {
              if (lesson.id === lessonId) {
                return { ...lesson, [tagType === LessonTagType.A ? 'tagA' : 'tagB']: tag };
              }
              return lesson;
            });
          });

            if (tagType === LessonTagType.A && !lessonTagsA.includes(tag)) {
                setLessonTagsA(prevTags => [...prevTags, tag]);
            } else if (tagType === LessonTagType.B && !lessonTagsB.includes(tag)) {
                setLessonTagsB(prevTags => [...prevTags, tag]);
            }
        }
    };

    const handleClickOpen = (message: string, action: (userIds: string[]) => void) => {
        setDialogMessage(message);
        setConfirmAction(() => action);
        setDialogOpen(true);
    };

    const handleDeleteRowsClick = (lessonIdsToDelete: string[], setSelectedRows: (rows: number[]) => void) => {
        handleClickOpen(t('lessonsDataTable.confirmDeleteLessons'), () => handleDeleteManyLessons(lessonIdsToDelete, setSelectedRows));
    }
    
    const handleDeleteManyLessons = async (lessonIds: string[], setSelectedRows: (rows: number[]) => void) => {
        const resp = await deleteManyLessons(lessonIds);
        if (resp?.success) {
            setLessons(prevLessons => prevLessons.filter(lesson => !lessonIds.includes(lesson.id)));
            setSelectedRows([]);
        }
    }

    const handleConfirm = () => {
        confirmAction();
        setDialogOpen(false);
    };

    return <Box>
    {fetchingLessons
    ? <CircularProgress />
    : <StyledMUIDataTable
        title={title}
        data={lessons}
        columns={lessonColumns}
        options={{
            responsive: 'standard',
            textLabels: {
                body: {
                  noMatch: t('dataTable.noMatch'),
                  toolTip: t('dataTable.toolTip'),
                  columnHeaderTooltip: column => t('dataTable.columnHeaderTooltip', { column: column.label })
                },
                pagination: {
                  next: t('dataTable.next'),
                  previous: t('dataTable.previous'),
                  rowsPerPage: t('dataTable.rowsPerPage'),
                  displayRows: t('dataTable.displayRows'),
                },
                toolbar: {
                  search: t('dataTable.search'),
                  downloadCsv: t('dataTable.downloadCsv'),
                  print: t('dataTable.print'),
                  viewColumns: t('dataTable.viewColumns'),
                  filterTable: t('dataTable.filterTable'),
                },
                filter: {
                  all: t('dataTable.all'),
                  title: t('dataTable.title'),
                  reset: t('dataTable.reset'),
                },
                viewColumns: {
                  title: t('dataTable.viewColumnsTitle'),
                  titleAria: t('dataTable.viewColumnsTitleAria'),
                },
                selectedRows: {
                  text: t('dataTable.selectedRowsText'),
                  delete: t('dataTable.delete'),
                  deleteAria: t('dataTable.deleteAria'),
                },
            },
            fixedHeader: false,
            onRowSelectionChange: (curSelectedRows, allRowsSelected, rowsSelected) => {
                if (rowsSelected) {
                    setParentLessonsSelected(rowsSelected);
                    setLessonsSelected(rowsSelected);
                }
            },
            customToolbarSelect: (selectedRows, displayData, setSelectedRowsFunc) => {
                // Store setSelectedRowsFunc in state when it's available
                setSetSelectedRows(() => setSelectedRowsFunc);
        
                return (
                    <Stack display={'flex'} flexDirection={'row'} justifyContent={'flex-end'}>
                        <IconButton onClick={() => handleDeleteRowsClick(selectedRows.data.map((d: any) => lessons[d.dataIndex].id), setSelectedRowsFunc)}><DeleteIcon /></IconButton>
                    </Stack>
                );
            }}
        }
        />    
    }
        <ConfirmationDialog open={dialogOpen} handleClose={() => setDialogOpen(false)} handleConfirm={handleConfirm} message={dialogMessage}/>
    </Box>
}